import { spellCount, spellCountLower } from "@/lib/spell-count";

/**
 * How the windows step talks about what the house has and what the connector
 * found.
 *
 * Pure and separate from the screen for the same reason `connector-issue.ts`
 * is: this is the copy a resident reads while holding a remote in the other
 * hand, and it is worth testing without a component-testing stack.
 *
 * The rule these all follow: never state a total on its own. The house scan
 * counts windows and blinds separately, the resident thinks in windows and
 * blinds, and the connector reports a flat list of actuators that says nothing
 * about which is which. A bare number sits in the gap between those three and
 * cannot be reconciled with any of them.
 */

/**
 * "four windows and two blinds", dropping whichever side is zero.
 *
 * Plenty of households have motorised windows and no motorised blinds, and
 * "four windows and no blinds" makes them read a clause about something they
 * do not own to find the number they came for.
 */
export function describeScan(windows: number, blinds: number): string {
  const parts: string[] = [];
  if (windows > 0) {
    parts.push(`${spellCountLower(windows)} ${windows === 1 ? "window" : "windows"}`);
  }
  if (blinds > 0) {
    parts.push(`${spellCountLower(blinds)} ${blinds === 1 ? "blind" : "blinds"}`);
  }
  return parts.join(" and ");
}

/**
 * What the discovery screen says about the actuators the connector exposed.
 *
 * The shortfall sentence used to read "Your house scan expects 7." and nothing
 * else. Two things were wrong with it. The number carried no noun, on a screen
 * headed with a count of products, inside a module called Windows, so it read
 * as seven windows: Beta Test House 5 has five, and the resident stopped there
 * rather than continue against a number they could not make sense of. And the
 * one cause it offered, a rescan, was not their cause, so following it got them
 * nowhere.
 *
 * So it now spends its words on the breakdown and the size of the gap, which
 * are the two things the resident can act on, and offers the rescan last as one
 * possible reason rather than the reason.
 */
export function describeDiscovery(found: number, windows: number, blinds: number): string {
  const expected = windows + blinds;

  if (expected === 0) {
    return "Your house scan doesn't list any motorised VELUX products, so there's nothing to compare against. Check this looks right.";
  }

  if (found >= expected) {
    return `That matches your house scan: ${describeScan(windows, blinds)}.`;
  }

  return (
    `Your house scan lists ${describeScan(windows, blinds)}, so ${spellCountLower(expected)} products in all. ` +
    `${spellCount(expected - found)} still to arrive. ` +
    `Anything paired after the connector started shows up only after a rescan.`
  );
}

/**
 * What to call one row in the found list.
 *
 * The KLG 300 integration hands Home Assistant every io actuator as a cover
 * with device class "window", blinds included: there is no blind class in it at
 * all. So "not a shade" is not evidence of being a window, and a house with
 * motorised blinds gets a list where some rows are lying.
 *
 * When the scan says the house has no motorised blinds, every actuator is a
 * window and the specific word is safe and better. Otherwise the honest word is
 * the general one, and the sentence above the list carries the breakdown.
 */
export function labelForCover(
  deviceClass: string | null | undefined,
  blindsExpected: number,
): string {
  if (deviceClass === "shade") {
    return "VELUX blind";
  }
  return blindsExpected === 0 ? "VELUX window" : "VELUX product";
}

/**
 * The identify loop's progress line: mapped windows against every product the
 * connector reported, with blinds set aside named beside it.
 *
 * Blinds are counted in the denominator on purpose. The loop opens every
 * product the connector knows, blinds included, so "five of seven" is what
 * the resident is actually walking through; hiding the two would make the
 * loop look finished two rounds early.
 */
export function describeMappingProgress(mapped: number, blinds: number, total: number): string {
  const head = `${spellCount(mapped)} of ${spellCountLower(total)} mapped`;
  if (blinds === 0) {
    return head;
  }
  return `${head}, ${spellCountLower(blinds)} ${blinds === 1 ? "blind" : "blinds"} set aside`;
}

/**
 * The closing line about blinds the resident set aside during the loop.
 *
 * Beta Test House 5, 18 September 2026: two electric blinds sat in the
 * connector's list beside five windows, the loop asked "which window moved" as
 * a blind rolled, and there was no answer to give. Setting a blind aside is
 * that answer. The household record has no place for a blind's identity yet,
 * so the honest promise is the small one: it keeps working from its remote,
 * and connecting it is VELUX's job later.
 */
export function describeBlindsSetAside(blinds: number): string {
  if (blinds === 0) {
    return "";
  }
  if (blinds === 1) {
    return "One blind was set aside. It stays on its remote for now, and VELUX will connect it later.";
  }
  return `${spellCount(blinds)} blinds were set aside. They stay on their remotes for now, and VELUX will connect them later.`;
}

/** Whether the identify loop has run out of products to open. */
export function mappingLoopDone(counts: {
  mapped: number;
  blinds: number;
  total: number;
  /** Products neither mapped nor set aside. */
  remaining: number;
}): boolean {
  return counts.remaining === 0 || counts.mapped + counts.blinds >= counts.total;
}
